import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import {
  readStoredRoleViewState,
  writeStoredRoleViewState,
  syntheticRolesForPreset,
  type RoleRow,
  type RoleViewPresetId,
  type RoleViewOverrideKind,
  type RoleViewStoredState,
} from "./roleViewOverridePresets";
import { activeFlatNamesToSessionRoleRows } from "./roleViewFlatAdapter";

type ProjectAccessId = string | number;

type InternalOverride =
  | { kind: "off" }
  | { kind: "preset"; preset: RoleViewPresetId }
  | {
      kind: "custom";
      flatNames: string[];
      projectAccessIds: ProjectAccessId[];
    };

function storedToInternal(s: RoleViewStoredState): InternalOverride {
  if (s.kind === "off") return { kind: "off" };
  if (s.kind === "preset") return { kind: "preset", preset: s.preset };
  return {
    kind: "custom",
    flatNames: s.flatNames,
    projectAccessIds: Array.isArray(s.projectAccessIds)
      ? s.projectAccessIds
      : [],
  };
}

function internalToStored(i: InternalOverride): RoleViewStoredState {
  if (i.kind === "off") return { v: 2, kind: "off" };
  if (i.kind === "preset") return { v: 2, kind: "preset", preset: i.preset };
  return {
    v: 2,
    kind: "custom",
    flatNames: i.flatNames,
    projectAccessIds: i.projectAccessIds,
  };
}

function syntheticForInternal(i: InternalOverride): RoleRow[] | null {
  if (i.kind === "off") return null;
  if (i.kind === "custom") {
    return activeFlatNamesToSessionRoleRows(i.flatNames);
  }
  return syntheticRolesForPreset(i.preset);
}

function syntheticProjectAccesses(projectAccessIds: ProjectAccessId[]) {
  return projectAccessIds.map((project_id) => ({
    project_id,
    admin: true,
    role: "RESPONSIBLE",
  }));
}

export type RoleViewOverrideContextValue = {
  viewKind: RoleViewOverrideKind;
  /** Last preset id when viewKind === "preset"; when "custom", still useful for UI default selection */
  activePreset: RoleViewPresetId;
  setPreset: (p: RoleViewPresetId) => void;
  applyCustomFlatNames: (
    flatNames: string[],
    projectAccessIds?: ProjectAccessId[],
  ) => void;
  clearOverride: () => void;
  isOverrideActive: boolean;
  effectiveCurrentUser:
    NonNullable<RootState["session"]["currentUser"]> | undefined;
  customFlatNames: string[] | null;
  /** Per-project admin grants when viewKind === "custom"; otherwise null. */
  customProjectAccessIds: ProjectAccessId[] | null;
};

const RoleViewOverrideContext =
  createContext<RoleViewOverrideContextValue | null>(null);

export function RoleViewOverrideProvider({
  children,
}: {
  children: ReactNode;
}) {
  const reduxUser = useSelector((s: RootState) => s.session.currentUser);
  const [internal, setInternal] = useState<InternalOverride>(() =>
    storedToInternal(readStoredRoleViewState()),
  );

  const persist = useCallback((next: InternalOverride) => {
    setInternal(next);
    writeStoredRoleViewState(internalToStored(next));
  }, []);

  const setPreset = useCallback(
    (p: RoleViewPresetId) => {
      if (p === "off") {
        persist({ kind: "off" });
        return;
      }
      persist({ kind: "preset", preset: p });
    },
    [persist],
  );

  const applyCustomFlatNames = useCallback(
    (flatNames: string[], projectAccessIds: ProjectAccessId[] = []) => {
      persist({
        kind: "custom",
        flatNames: Array.from(new Set(flatNames)),
        projectAccessIds: Array.from(
          new Map(
            projectAccessIds
              .filter((id) => id != null && id !== "")
              .map((id) => [String(id), id]),
          ).values(),
        ),
      });
    },
    [persist],
  );

  const clearOverride = useCallback(() => {
    persist({ kind: "off" });
  }, [persist]);

  const value = useMemo<RoleViewOverrideContextValue>(() => {
    const viewKind: RoleViewOverrideKind =
      internal.kind === "off"
        ? "off"
        : internal.kind === "preset"
          ? "preset"
          : "custom";

    const activePreset: RoleViewPresetId =
      internal.kind === "preset" ? internal.preset : "off";

    const synth = syntheticForInternal(internal);
    let effectiveCurrentUser = reduxUser;
    if (reduxUser && synth !== null) {
      effectiveCurrentUser = { ...reduxUser, roles: synth };
      // Custom simulation replaces per-project grants so the Projects access
      // panel selections drive useProjectPermissions. Presets must not keep the
      // developer's real grants (e.g. project_leader_selected would otherwise
      // inherit full admin on the wrong projects).
      if (internal.kind === "custom") {
        effectiveCurrentUser = {
          ...effectiveCurrentUser,
          project_accesses: syntheticProjectAccesses(internal.projectAccessIds),
        };
      } else if (internal.kind === "preset") {
        effectiveCurrentUser = {
          ...effectiveCurrentUser,
          project_accesses: [],
        };
      }
    }

    return {
      viewKind,
      activePreset,
      setPreset,
      applyCustomFlatNames,
      clearOverride,
      isOverrideActive: internal.kind !== "off",
      effectiveCurrentUser,
      customFlatNames: internal.kind === "custom" ? internal.flatNames : null,
      customProjectAccessIds:
        internal.kind === "custom" ? internal.projectAccessIds : null,
    };
  }, [reduxUser, internal, setPreset, applyCustomFlatNames, clearOverride]);

  return (
    <RoleViewOverrideContext.Provider value={value}>
      {children}
    </RoleViewOverrideContext.Provider>
  );
}

export function useRoleViewOverride(): RoleViewOverrideContextValue | null {
  return useContext(RoleViewOverrideContext);
}

export function useRoleViewEffectiveCurrentUser():
  NonNullable<RootState["session"]["currentUser"]> | undefined {
  const reduxUser = useSelector((s: RootState) => s.session.currentUser);
  const ctx = useContext(RoleViewOverrideContext);
  if (!ctx) return reduxUser;
  return ctx.effectiveCurrentUser;
}
